import type {State} from 'src/reducers';
import type {
  AtsEntity,
  EntityType,
  AttributeDataType,
  RelatedEntityAttributeSchema,
  EntityMappingsByName,
} from 'src/types/ats-entities';
import type {DynamicLabels} from 'src/action-creators/dynamic-labels';
import type {NamespacedGlobalVariable} from 'src/types/namespaced-global-variables';
import type {GlobalVariableState} from 'src/reducers/global-variables';

import {createSelector} from 'reselect';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';

import {peopleEntityTypesSet} from 'src/types/ats-entities';
import {
  selectEntityMappingsByName,
  eventEntityType,
  selectRelatedEntityAttributesForEntityType,
} from 'src/selectors/ats-entities';
import {getAttributeRelationshipPath} from 'src/utils/entities';


export const getGlobalVariables = (state: State): GlobalVariableState =>
  state.globalVariables;

export const getNamespacedGlobalVariables: (
  arg1: State,
) => NamespacedGlobalVariable[] = (state) => state.namespacedGlobalVariables;

type VariableLevelDepthKeys = keyof typeof variableLevelDepths;
type VariableLevelDepthValues =
  typeof variableLevelDepths[keyof typeof variableLevelDepths];

const variableLevelDepths = {
  scheduling: 1,
  globals: 0,
  audience: 1,
  rte: 2,
  header: 2,
  recipient: 1,
  sender: 2,
} as const;

type DynamicLabelFilters = {
  dedup?: boolean;
  attributeSet?: 'all' | 'recipient';
  dataType?: AttributeDataType | AttributeDataType[];
  depth?: VariableLevelDepthKeys | VariableLevelDepthValues;
  hideGlobals?: boolean;
  hideHiddenFields?: boolean;
  onlyInternals?: boolean;
};

type DynamicLabelSelector = (arg1: State, arg2: EntityType) => DynamicLabels;

export const createDynamicLabelSelector = (
  filters: DynamicLabelFilters = Object.freeze({}),
): DynamicLabelSelector =>
  // @ts-expect-error - TS2322 - Type 'OutputSelector<any, Partial<Record<EntityType, AtsEntity>>, (res1: EntityType, res2: DynamicLabels, res3: RelatedEntityAttributeSchema[], res4: NamespacedGlobalVariable[]) => Partial<...>>' is not assignable to type 'DynamicLabelSelector'.
  createSelector<
    State,
    EntityType,
    DynamicLabels,
    RelatedEntityAttributeSchema[],
    NamespacedGlobalVariable[],
    EntityMappingsByName,
    EntityType
  >(
    selectRelatedEntityAttributesForEntityType,
    // @ts-expect-error - TS2345 - Argument of type '(arg1: any) => NamespacedGlobalVariable[]' is not assignable to parameter of type 'ParametricSelector<any, EntityType, RelatedEntityAttributeSchema[]>'.
    getNamespacedGlobalVariables,
    selectEntityMappingsByName,
    // @ts-expect-error - TS7006 - Parameter '_state' implicitly has an 'any' type. | TS7006 - Parameter 'baseEntityType' implicitly has an 'any' type.
    (_state, baseEntityType) => baseEntityType,
    (attributes, namespacedGlobalVariables, mappings, baseEntityType) => {
      const {attributeSet = 'all', hideHiddenFields = true} = filters;

      /*
    let fields =
      (baseEntityType &&
        attributes[baseEntityType] &&
        attributes[baseEntityType][attributeSet]) ||
      [];
    */

      // dedup and prefer shorter paths
      if (filters.dedup) {
        // @ts-expect-error - TS2322 - Type 'unknown[]' is not assignable to type 'EntityType'.
        attributes = Array.from(
          attributes
            // @ts-expect-error - TS2339 - Property 'reduce' does not exist on type 'EntityType'. | TS7006 - Parameter 'map' implicitly has an 'any' type. | TS7006 - Parameter 'attr' implicitly has an 'any' type.
            .reduce((map, attr) => {
              const key =
                attr.resolvedRelationshipPath
                  // @ts-expect-error - TS7006 - Parameter 'rel' implicitly has an 'any' type.
                  .map((rel) => String(rel.id))
                  .join('/') +
                '/' +
                attr.id;
              const prevAttr = map.get(key);
              if (
                prevAttr &&
                prevAttr.relationshipPath.length < attr.relationshipPath.length
              ) {
                attr = prevAttr;
              }
              return map.set(key, attr);
            }, new Map())
            .values(),
        );
      }

      // @ts-expect-error - TS2339 - Property 'map' does not exist on type 'EntityType'. | TS7006 - Parameter 'field' implicitly has an 'any' type.
      let fields = attributes.map((field) => {
        // TODO: it would be nice to change this field's name from "relatedEntityType" to "entityType"
        const {entity_type, relationshipPath, resolvedRelationshipPath} = field;
        const fullyFormedDisplayName = [
          ...resolvedRelationshipPath.map(
            // @ts-expect-error - TS7006 - Parameter 'relationship' implicitly has an 'any' type.
            (relationship) => relationship.display_name,
          ),
          field.display_name,
        ].join('/');
        const fullyFormedName = [
          ...relationshipPath,
          field.attribute_name,
        ].join('/');
        const resolvedId =
          field.resolvedRelationshipPath
            // @ts-expect-error - TS7006 - Parameter 'rel' implicitly has an 'any' type.
            .map((rel) => String(rel.id))
            .join('/') +
          '/' +
          field.id;

        return {
          attributeId: field.id,
          resolvedId,
          source: 'member',
          // TODO (rng) - Added this because some components use source and others use resource, clean up later
          resource: 'member',
          entity_type,
          // TODO (kyle): figure out if `mapping` is necessary
          // @ts-expect-error - TS2339 - Property 'sense_name' does not exist on type 'RelatedEntityAttributeSchema'.
          sense_name: mappings[entity_type]?.sense_name,
          display_entity_type: entity_type,
          mapping: mappings[entity_type],
          type: field.data_type,
          name: fullyFormedDisplayName,
          label: fullyFormedDisplayName,
          value: fullyFormedName,
          fieldLabel: field.display_name,
          rteValue: `<${fullyFormedName}>`,
          template: '<$>',
          related_entities: field.relationshipPath,
          baseEntityType,
          entityRelationshipPath: getAttributeRelationshipPath(
            fullyFormedDisplayName,
            // @ts-expect-error - TS2345 - Argument of type 'NamespacedGlobalVariable[]' is not assignable to parameter of type 'EntityType'.
            baseEntityType,
            mappings,
          ),
          hidden: field.hidden,
          attributeName: field.attribute_name,
          is_join_attribute: field.is_join_attribute,
          designations: field.designations,
        };
      });

      if (!filters.hideGlobals) {
        // global variables are wired up here
        //fields = fields.concat(
        const gvs = namespacedGlobalVariables
          .filter(
            ({anchor_entity_type}) => anchor_entity_type === baseEntityType,
          )
          .map(
            ({
              fully_formed_name,
              related_entities,
              global_variable: {id, type, entity_type},
              anchor_entity_type,
            }) => {
              //we are resolving the global variable id so that they don't get removed
              //during deduplication.
              //see AEP-2692
              const mapping = mappings[anchor_entity_type];
              // @ts-expect-error - TS2339 - Property 'related_entities' does not exist on type 'RelatedEntityAttributeSchema'.
              const mappingRelatedEntities = mapping?.related_entities ?? [];
              const relatedEntitiesByName = new Map(
                // @ts-expect-error - TS7006 - Parameter 'related_entity' implicitly has an 'any' type.
                mappingRelatedEntities.map((related_entity) => [
                  related_entity.name,
                  related_entity,
                ]),
              );
              const resolvedId =
                related_entities.length > 0 && relatedEntitiesByName.size > 0
                  ? `${related_entities
                      .map(
                        // @ts-expect-error - TS7006 - Parameter 'related_entity' implicitly has an 'any' type.
                        (related_entity) =>
                          // @ts-expect-error - TS2339 - Property 'id' does not exist on type '{}'.
                          relatedEntitiesByName.get(related_entity)?.id,
                      )
                      .join('/')}/${id}`
                  : `global/${id}`;
              return {
                resolvedId,
                entity_type,

                // not using anchor_entity_type because we want global variables to be separate in modal
                display_entity_type: 'global_variable',
                source: 'global_variable',
                sense_name: 'global_variable',

                // TODO (rng) - Added this because some components use source and others use resource, clean up later
                resource: 'global_variable',
                type,
                mapping: {
                  name: 'global_variable',
                  display_name: 'Global Variable',
                  display_name_plural: 'Global Variables',
                  sense_name: 'global_variable',
                  url: 'global_variable',
                  event_entity: false,
                  is_person: false,
                  related_entities: [],
                },
                name: fully_formed_name,
                label: fully_formed_name,
                value: fully_formed_name,
                //NOTE:(diwakersurya) lets keep this fully formed name for now.
                //if required, need to figure out how to extract only the display
                //name for global variable
                fieldLabel: fully_formed_name,
                rteValue: `<${fully_formed_name}>`,
                template: '<$>',
                related_entities,
                baseEntityType,
                entityRelationshipPath: getAttributeRelationshipPath(
                  fully_formed_name,
                  // @ts-expect-error - TS2345 - Argument of type 'NamespacedGlobalVariable[]' is not assignable to parameter of type 'EntityType'.
                  baseEntityType,
                  mappings,
                ),
                hidden: false,
                is_join_attribute: false,
              };
            },
          );
        fields = fields.concat(gvs);
      }

      if (filters.onlyInternals) {
        // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
        fields = fields.filter((field) => field.mapping.url === 'internals');
      }

      if (filters.attributeSet === 'recipient') {
        fields = fields.filter(
          // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
          (field) =>
            field.entity_type && peopleEntityTypesSet.has(field.entity_type),
        );
      }

      if (filters.dataType) {
        let targetTypes = filters.dataType;
        if (isString(targetTypes)) {
          targetTypes = [targetTypes];
        }
        // This is a specific detail of global variables:
        // we allow them as long as their entity relation matches
        // even if their dataType is mismatched (ENGAGE-2263)
        fields = fields.filter(
          // @ts-expect-error - TS7031 - Binding element 'type' implicitly has an 'any' type. | TS7031 - Binding element 'source' implicitly has an 'any' type.
          ({type, source}) =>
            source === 'global_variable' || targetTypes.includes(type),
        );
      }

      // TODO (kyle): this was invalidating '0'
      if ('depth' in filters) {
        // Default to the current limit of variable depth
        let targetDepth: VariableLevelDepthValues = 2;

        if (isNumber(filters.depth)) {
          targetDepth = filters.depth;
        }
        if (isString(filters.depth)) {
          targetDepth = variableLevelDepths[filters.depth];
        }
        fields = fields.filter(
          // @ts-expect-error - TS7031 - Binding element 'related_entities' implicitly has an 'any' type.
          ({related_entities}) =>
            related_entities && related_entities?.length <= targetDepth,
        );
      }

      if (hideHiddenFields) {
        // @ts-expect-error - TS7031 - Binding element 'hidden' implicitly has an 'any' type.
        fields = fields.filter(({hidden}) => !hidden);
      }

      // @ts-expect-error - TS7006 - Parameter 'a' implicitly has an 'any' type. | TS7006 - Parameter 'b' implicitly has an 'any' type.
      fields.sort((a, b) => a.label.localeCompare(b.label));

      return fields;
    },
  );

const selectAllFieldsAtAudienceLevel = createDynamicLabelSelector({
  depth: 'audience',
});

const selectAllFieldsAtAudienceLevelWithoutGlobalVariables =
  createDynamicLabelSelector({
    depth: 'audience',
    hideGlobals: true,
  });

export const selectAllFieldsIncludingHidden: DynamicLabelSelector =
  createDynamicLabelSelector({
    hideHiddenFields: false,
  });

export const selectWorkflowEntityMapping: (
  arg1: State,
) => AtsEntity | Record<any, any> = createSelector(
  eventEntityType,
  selectEntityMappingsByName,
  (entityType, mappings) => (mappings[entityType] ? mappings[entityType] : {}),
);

export const selectAudienceLabels: DynamicLabelSelector = createSelector<
  State,
  EntityType,
  DynamicLabels,
  DynamicLabels,
  DynamicLabels
  // @ts-expect-error - TS2769 - No overload matches this call.
>(selectAllFieldsAtAudienceLevel, (dynamicLabels) =>
  // @ts-expect-error - TS2339 - Property 'filter' does not exist on type 'EntityType'. | TS7031 - Binding element 'type' implicitly has an 'any' type.
  dynamicLabels.filter(({type}) => type !== 'text' && type !== 'html'),
);

export const selectAudienceListFields: DynamicLabelSelector = createSelector<
  State,
  EntityType,
  DynamicLabels,
  DynamicLabels,
  DynamicLabels
  // @ts-expect-error - TS2769 - No overload matches this call.
>(selectAllFieldsAtAudienceLevelWithoutGlobalVariables, (dynamicLabels) =>
  // @ts-expect-error - TS2339 - Property 'filter' does not exist on type 'EntityType'. | TS7031 - Binding element 'type' implicitly has an 'any' type.
  dynamicLabels.filter(({type}) => type !== 'text' && type !== 'html'),
);

export const selectDynamicLabels: DynamicLabelSelector =
  createDynamicLabelSelector();
